/**
 * @fileoverview V2 Review Step — Summary of all tracked sections with completion status.
 *
 * Shows each audit-tracked section grouped by status:
 * - Needs Review (null) — amber, with "Review" button to navigate
 * - Found — green, with "Edit" button
 * - Not Available — gray, with "Edit" button
 *
 * Finish button is enabled only when all sections are reviewed.
 */
import {
  AlertCircle,
  CheckCircle,
  ChevronRight,
  MinusCircle,
} from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  CaseSetupMessagingContext,
  CaseSetupMessagingProvider,
} from '@/components/case-ingestion/context/CaseSetupMessagingContext';
import { ReviewMessagingPanel } from '@/components/case-ingestion/steps/review/ReviewMessagingPanel';
import {
  useCompleteCaseIngestionMutation,
  useGetCaseIngestionQuery,
} from '@/services/api/caseIngestionService';
import { useGetMatterServicesQuery } from '@/services/api/mattersService';
import {
  INGESTION_SECTIONS,
  type IngestionSection,
} from '@/services/types/case-ingestion-types';


import { FormSection } from '../components/form';
import { STEPS } from '../navigation/steps';
import { useIngestion } from '../state/IngestionProvider';

const SECTION_LABELS: Record<IngestionSection, string> = {
  first_party_coverage: 'First Party Coverage',
  third_party_coverage: 'Third Party Coverage',
  health_insurance: 'Health Insurance',
  police_report: 'Police Report',
  medical_providers: 'Medical Providers',
  retainer_document: 'Retainer Agreement',
  hipaa_document: 'HIPAA Directive',
  companion_cases: 'Companion Cases',
};

const SECTION_TO_STEP_ID: Record<IngestionSection, string> = {
  companion_cases: 'companions',
  police_report: 'liability',
  medical_providers: 'treatment',
  first_party_coverage: 'coverage',
  third_party_coverage: 'coverage',
  health_insurance: 'coverage',
  retainer_document: 'documents',
  hipaa_document: 'documents',
};

const DEFAULT_SECTION_STATUSES = {
  first_party_coverage: null,
  third_party_coverage: null,
  health_insurance: null,
  police_report: null,
  medical_providers: null,
  retainer_document: null,
  hipaa_document: null,
  companion_cases: null,
} as const;

interface SectionGroupConfig {
  title: string;
  emptyLabel: string;
  buttonLabel: string;
  containerClassName: string;
  dividerClassName: string;
  rowButtonClassName: string;
  labelClassName: string;
  iconClassName: string;
  Icon: typeof AlertCircle;
}

interface SectionGroupProps {
  config: SectionGroupConfig;
  sections: IngestionSection[];
  onSelect: (section: IngestionSection) => void;
}

const REVIEW_GROUPS: Record<'needsReview' | 'found' | 'notAvailable', SectionGroupConfig> = {
  needsReview: {
    title: 'Needs Review',
    emptyLabel: 'Review',
    buttonLabel: 'Review',
    containerClassName: 'border-amber-200 bg-amber-50',
    dividerClassName: 'divide-amber-200',
    rowButtonClassName:
      'text-amber-700 hover:bg-amber-100 hover:text-amber-900',
    labelClassName: 'text-amber-700',
    iconClassName: 'text-amber-600',
    Icon: AlertCircle,
  },
  found: {
    title: 'Found',
    emptyLabel: 'Edit',
    buttonLabel: 'Edit',
    containerClassName: 'border-green-200 bg-green-50',
    dividerClassName: 'divide-green-200',
    rowButtonClassName:
      'text-green-700 hover:bg-green-100 hover:text-green-900',
    labelClassName: 'text-green-700',
    iconClassName: 'text-green-600',
    Icon: CheckCircle,
  },
  notAvailable: {
    title: 'Not Available',
    emptyLabel: 'Edit',
    buttonLabel: 'Edit',
    containerClassName: 'border-zinc-200 bg-zinc-50',
    dividerClassName: 'divide-zinc-200',
    rowButtonClassName:
      'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700',
    labelClassName: 'text-zinc-500',
    iconClassName: 'text-zinc-400',
    Icon: MinusCircle,
  },
};

function getStepIndex(section: IngestionSection): number {
  const stepId = SECTION_TO_STEP_ID[section];

  return STEPS.findIndex((step) => step.id === stepId);
}

function getReviewDescription(needsReviewCount: number): string {
  if (needsReviewCount === 0) {
    return 'All sections reviewed — ready to finish';
  }

  const noun = needsReviewCount === 1 ? 'section' : 'sections';
  const verb = needsReviewCount === 1 ? 'needs' : 'need';

  return `${needsReviewCount} ${noun} ${verb} review`;
}

const SectionGroup = ({
  config,
  sections,
  onSelect,
}: SectionGroupProps): React.JSX.Element | null => {
  if (sections.length === 0) {
    return null;
  }

  const { Icon } = config;

  return (
    <div className="space-y-1.5">
      <h3 className="text-xs font-semibold text-zinc-900">{config.title}</h3>
      <div
        className={`overflow-hidden rounded-lg border ${config.containerClassName} divide-y ${config.dividerClassName}`}
      >
        {sections.map((section) => (
          <div
            key={section}
            className="flex items-center justify-between px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <Icon className={`h-3.5 w-3.5 shrink-0 ${config.iconClassName}`} />
              <span className={`text-sm font-medium ${config.labelClassName}`}>
                {SECTION_LABELS[section]}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onSelect(section)}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${config.rowButtonClassName}`}
            >
              {config.buttonLabel}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Wrapper that conditionally provides CaseSetupMessagingContext
 * when the automated_introduction workflow is enabled.
 */
export const ReviewStep: React.FC = () => {
  const { state } = useIngestion();
  const matterId = state.matterId!;

  const { data: matterServices } = useGetMatterServicesQuery(
    { matterId },
  );

  const isAutoIntroEnabled = useMemo(() => {
    if (!matterServices) return false;
    return matterServices.some((svc) =>
      svc.is_active &&
      svc.workflows.some(
        (w) => w.workflow_code === 'automated_introduction' && w.is_enabled,
      ),
    );
  }, [matterServices]);

  if (isAutoIntroEnabled) {
    return (
      <CaseSetupMessagingProvider matterId={matterId}>
        <ReviewStepContent />
      </CaseSetupMessagingProvider>
    );
  }

  return <ReviewStepContent />;
};

const ReviewStepContent: React.FC = () => {
  const { state, dispatch, registerSave } = useIngestion();
  const navigate = useNavigate();
  const matterId = state.matterId!;

  // Show messaging panel when CaseSetupMessagingProvider is present
  const messagingCtx = useContext(CaseSetupMessagingContext);
  const showMessagingPanel = !!messagingCtx;

  const { data: auditRecord } = useGetCaseIngestionQuery(matterId);
  const [completeIngestion] = useCompleteCaseIngestionMutation();

  const sectionStatuses = auditRecord?.sections ?? DEFAULT_SECTION_STATUSES;

  const needsReview = useMemo(
    () => INGESTION_SECTIONS.filter((section) => sectionStatuses[section] === null),
    [sectionStatuses],
  );
  const found = useMemo(
    () => INGESTION_SECTIONS.filter((section) => sectionStatuses[section] === 'found'),
    [sectionStatuses],
  );
  const notAvailable = useMemo(
    () =>
      INGESTION_SECTIONS.filter(
        (section) => sectionStatuses[section] === 'not_found',
      ),
    [sectionStatuses],
  );

  const canFinish = needsReview.length === 0;

  useEffect(() => {
    registerSave(async () => true);

    return () => registerSave(null);
  }, [registerSave]);

  const goToSection = useCallback(
    (section: IngestionSection): void => {
      dispatch({ type: 'GO_TO_STEP', index: getStepIndex(section) });
    },
    [dispatch],
  );

  const handleFinish = useCallback(async (): Promise<void> => {
    if (!canFinish) {
      toast.error('Please review all sections before finishing');
      return;
    }

    try {
      // Backend handles status transition to INITIAL_REVIEW atomically
      await completeIngestion({ matterId }).unwrap();
      toast.success('Ingestion complete! Matter moved to Initial Review.');
      navigate(`/matters/${matterId}`);
    } catch {
      toast.error('Failed to complete ingestion');
    }
  }, [canFinish, matterId, navigate, completeIngestion]);

  return (
    <div className="flex gap-4 min-h-0">
      {/* Left column: Review checklist */}
      <div className="flex-1 min-w-0 space-y-8">
      <FormSection
        title="Review"
        description={getReviewDescription(needsReview.length)}
      >
        <div className="space-y-4">
          {canFinish && (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
              <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
              <p className="text-sm font-medium text-green-700">
                All sections reviewed. Click Finish to complete ingestion.
              </p>
            </div>
          )}

          <SectionGroup
            config={REVIEW_GROUPS.needsReview}
            sections={needsReview}
            onSelect={goToSection}
          />

          {needsReview.length > 0 && (
            <p className="text-xs text-amber-600">
              Review all sections above to complete ingestion
            </p>
          )}

          <SectionGroup
            config={REVIEW_GROUPS.found}
            sections={found}
            onSelect={goToSection}
          />

          <SectionGroup
            config={REVIEW_GROUPS.notAvailable}
            sections={notAvailable}
            onSelect={goToSection}
          />
        </div>
      </FormSection>

      <button
        type="button"
        onClick={handleFinish}
        disabled={!canFinish}
        className={
          `inline-flex items-center justify-center rounded-lg px-6 py-2.5 text-sm font-semibold transition-colors duration-150 ${
          canFinish
            ? 'bg-green-600 text-white shadow-sm hover:bg-green-700'
            : 'cursor-not-allowed bg-zinc-200 text-zinc-500'}`
        }
      >
        {canFinish ? 'Finish Ingestion' : 'Complete all sections to finish'}
      </button>
      </div>

      {/* Right column: Messaging panel */}
      {showMessagingPanel && matterId && (
        <div className="w-[380px] shrink-0 rounded-xl border border-zinc-200 overflow-hidden bg-white">
          <ReviewMessagingPanel matterId={matterId} />
        </div>
      )}
    </div>
  );
}
